import { TaskDefinition } from '@/feature/execution-board/types/task'

import { isTaskDetailViewable } from '../../utils/taskMapVisibility'
import { TaskStatus } from '../CreateTaskModal'

// Explainer shown on the Progress column header tooltip
export const PROGRESS_TOOLTIP_TEXT =
  'Live estimate based on distance remaining to final location — can move up or down as the vehicle travels. Once a task is past its expiry date, progress is no longer shown.'

// Status values that allow task deletion
export const DELETABLE_STATUSES = [
  TaskStatus.REQUESTED,
  TaskStatus.SCHEDULED,
  TaskStatus.REJECTED,
  TaskStatus.CANCELLED,
]

// Get the starting location (the one with no predecessor) from task locations
export const getLocationFrom = (task: TaskDefinition) => {
  return task.taskDefinition.locations.find((location) => location.predecessor === null)
}

// Get the destination location (the one whose predecessor is the starting location)
export const getLocationTo = (task: TaskDefinition) => {
  const predecessorLocation = getLocationFrom(task)
  return task.taskDefinition.locations.find(
    (location) => location.predecessor === predecessorLocation?.id,
  )
}

// Check if a task can be deleted based on its status
export const canDeleteTask = (task: TaskDefinition): boolean => {
  return DELETABLE_STATUSES.includes(task.status as TaskStatus)
}

// Whether the eye action should be offered for a task. The action opens the task focus modal in
// place, so this is not tied to what the Observatory's live map layer renders — completed tasks
// are reviewable too (BRI-1477). See DETAIL_VIEWABLE_TASK_STATUSES.
export const canViewTaskDetails = (task: TaskDefinition): boolean => {
  return isTaskDetailViewable(task.status)
}

// Check if the status dropdown should be disabled for a task
export const isStatusDropdownDisabled = (task: TaskDefinition): boolean => {
  return (
    !!task.onwardAssignedToOrg ||
    task.status === TaskStatus.COMPLETED ||
    task.status === TaskStatus.CANCELLED
  )
}

// Count the number of stops (locations) on a task
export const getStopsCount = (task: TaskDefinition): number => {
  return task.taskDefinition.locations.length
}

// Filter tasks to get only those with assigned vehicles that are not rejected or completed
export const getActiveVehicleIds = (results: TaskDefinition[] | undefined): number[] => {
  return (
    results
      ?.filter(
        (record) =>
          record.assignedVehicle &&
          ![TaskStatus.REJECTED, TaskStatus.COMPLETED, TaskStatus.CANCELLED].includes(
            record.status as TaskStatus,
          ),
      )
      .map((record) => record.assignedVehicle!.id) ?? []
  )
}
